import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { PREVIEW_DEVICE_WIDTH, type PreviewDevice } from './DevicePreviewToggle';

interface PaneSize {
  width: number;
  height: number;
}

/** The pane's inner size, kept up to date; null until it has been measured (and wherever layout reads 0, as jsdom). */
function usePaneSize() {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<PaneSize | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const width = el.clientWidth;
      const height = el.clientHeight;
      setSize((current) =>
        width <= 0 || height <= 0
          ? null
          : current?.width === width && current.height === height
            ? current
            : { width, height },
      );
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, size };
}

/** How far a preset is scaled down to fit the pane: never up, and not at all before the pane is measured. */
export function deviceScale(deviceWidth: number | null, paneWidth: number | null): number {
  if (deviceWidth === null || paneWidth === null || paneWidth <= 0) return 1;
  return Math.min(1, paneWidth / deviceWidth);
}

export interface ClassicPreviewFrameProps {
  /** Mobile is a 375px column; any other preset fills the pane. */
  device: PreviewDevice;
  /** Stands in for the frame while there is no page to load. */
  empty?: ReactNode;
  children?: ReactNode;
}

/**
 * The frame the theme editor has always had, for its plain Desktop / Mobile pair: Desktop fills the pane, Mobile is a
 * bordered 375px column (a 373px viewport) centred in it, never scaled. Used unless the custom pack lab is in play.
 */
export function ClassicPreviewFrame({ device, empty, children }: ClassicPreviewFrameProps) {
  const mobile = device === 'mobile';
  return (
    <div className={'flex flex-1 overflow-hidden bg-neutral-950 ' + (mobile ? 'justify-center px-4 py-4' : '')}>
      {children ? (
        <div
          className={
            mobile
              ? 'h-full w-[375px] overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900 shadow-2xl'
              : 'h-full w-full'
          }
        >
          {children}
        </div>
      ) : (
        empty
      )}
    </div>
  );
}

export interface DevicePreviewFrameProps {
  device: PreviewDevice;
  children: ReactNode;
}

// A ring, not a border: it takes no room, so the page inside gets exactly the preset's width.
const FIXED_CHROME = 'overflow-hidden rounded-lg bg-neutral-900 shadow-2xl ring-1 ring-neutral-800';

/**
 * The preview pane sized for a device preset. Desktop fills the pane. A fixed-width preset gets a framed column of
 * exactly its width, centred; one wider than the pane is drawn with `transform: scale()` so it fits, its own viewport
 * staying at the preset's width and as tall as the pane allows once scaled. The tree is the same for every preset, so
 * switching never remounts (and so reloads) the iframe inside.
 */
export function DevicePreviewFrame({ device, children }: DevicePreviewFrameProps) {
  const width = PREVIEW_DEVICE_WIDTH[device];
  const fixed = width !== null;
  const { ref, size } = usePaneSize();
  const scale = deviceScale(width, size?.width ?? null);

  const style: CSSProperties = fixed
    ? {
        position: 'absolute',
        top: 0,
        left: '50%',
        width,
        height: size ? size.height / scale : '100%',
        transform: `translateX(-50%) scale(${scale})`,
        transformOrigin: 'top center',
      }
    : { width: '100%', height: '100%' };

  return (
    <div className={'flex flex-1 overflow-hidden bg-neutral-950 ' + (fixed ? 'px-4 py-4' : '')}>
      <div ref={ref} className="relative h-full w-full overflow-hidden">
        <div
          data-testid="device-frame"
          data-device={device}
          data-scale={scale.toFixed(3)}
          style={style}
          className={fixed ? FIXED_CHROME : ''}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
